import { Injectable } from '@angular/core';
import { FormGroup, FormBuilder, Validators, FormControl } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';

import { Utils } from '../../../../shared/fork-common-controls/utils.service';
import {
  CommonAddress,
  Device,
  PersonalLoanRegistrationRequest,
  UpdateBorrowerRequest } from '../../../../shared/models';

import CreditScoreEstimateEnum = PersonalLoanRegistrationRequest.CreditScoreEstimateEnum;

import { UlaValidators } from '../../../../common-library/validators/ula-validators';
import { BorrowerService } from '../../../../shared/services/borrower/borrower.service';
import { FormValidationService } from '../../../../shared/fork-common-controls/form-validation.service';
import { UlaEmailControl } from '../../../../shared/ulaEmailControl';

@Injectable()
export class RegisterFormProvider {
  public static config: { state: any } = { state: {} };
  private static registerForm: FormGroup = null;

  constructor(
    private route: ActivatedRoute,
    private formBuilder: FormBuilder,
    private borrowerService: BorrowerService,
    private registerFormValidationService: FormValidationService,
    private utils: Utils) {

    if (!RegisterFormProvider.registerForm) {
      this.init();
    }
  }

  public getUpdateBorrowerPayload(): UpdateBorrowerRequest {
    const {
      firstName,
      lastName,
      suffix,
      email,
      address,
      addressUnit,
      state,
      city,
      zipCode,
      occupancyStatus,
      paymentAmount,
      dob,
      yearlyIncome,
      phoneNumber
    } = RegisterFormProvider.registerForm.value;

    // TODO: LDTSULA-2766 covers adding the phoneType field.

    const commonAddress: CommonAddress = {
      addressLine1: address,
      addressLine2: addressUnit,
      state,
      city,
      zipCode
    };

    const request: UpdateBorrowerRequest = {
      emailAddress: email,
      firstName,
      nameSuffix: suffix,
      lastName,
      address: commonAddress,
      phoneNumber,
      dateOfBirth: new Date(dob),
      occupancyStatus,
      homeMonthlyPayment: Utils.currencyStringToNumber(paymentAmount),
      annualIncome: Utils.currencyStringToNumber(yearlyIncome)
    };

    return request;
  }

  public getRegisterMortgageBorrowerForPLApplicationPayload(
    jornayaLeadID: string,
    eSignConsentAccepted: boolean,
    leadGuid?: string): PersonalLoanRegistrationRequest {

    const {
      mortgageBorrowerGuid,
      loanAmount,
      loanPurpose,
      firstName,
      lastName,
      suffix,
      email,
      phoneNumber,
      address,
      addressUnit,
      city,
      state,
      zipCode,
      dob,
      occupancyStatus,
      paymentAmount,
      yearlyIncome,
      contactAgreement
    } = RegisterFormProvider.registerForm.value;

    const addressModel: CommonAddress = {
      addressLine1: address,
      addressLine2: addressUnit,
      city,
      state,
      zipCode
    };

    const deviceInfo: Device = {
      latitude: 12,  // TODO: get this information
      longitude: 20, // TODO: get this information
      blackBoxId: ''     // TODO: LDTSULA-1190 need to add blackboxId here matching id in hidden field
    };

    const request: PersonalLoanRegistrationRequest = {
      epsUserGuid: mortgageBorrowerGuid,
      loanPurpose,
      requestedLoanAmount: Utils.currencyStringToNumber(loanAmount),
      emailAddress: email,
      firstName,
      nameSuffix: suffix === 'null' ? '' : suffix,
      lastName,
      address: addressModel,
      phoneNumber: Utils.formatPhoneNumberForBackEnd(phoneNumber),
      dateOfBirth: new Date(dob),
      leadGuid,
      occupancyStatus,
      homeMonthlyPayment: Utils.currencyStringToNumber(paymentAmount),
      annualIncome: Utils.currencyStringToNumber(yearlyIncome),
      creditScoreEstimate: CreditScoreEstimateEnum['Excellent'], // TODO get credit estimate
      jornayaLeadID,
      isPhoneContactConsentAccepted: true,
      termsOfUseAccepted: true,
      eSignConsentAccepted,
      tcpaDisclosureAccepted: contactAgreement,
      creditReportAuthorizationAccepted: true,
      deviceInfo: deviceInfo
    };

    return request;
  }

  public init(isReapplying: boolean = false): void {
    RegisterFormProvider.registerForm = (
      isReapplying ?
      this.getReapplyFormGroup() :
      this.getRegistrationFormGroup()
    );
  }

  public getForm(): FormGroup {
    return RegisterFormProvider.registerForm;
  }

  private getRegistrationFormGroup(): FormGroup {
    const passwordValidators = UlaValidators.getAllPasswordValidators();
    const occupancyStatusControl = new FormControl('', Validators.required);
    const stateControl = new FormControl('', [Validators.required, UlaValidators.getStateLicensingValidator(RegisterFormProvider.config)]);
    const passwordControl = new FormControl('', passwordValidators);
    const emailControl = new UlaEmailControl('', UlaValidators.getAllEmailValidators(this.registerFormValidationService));

    return this.formBuilder.group({
      // let's get started fields
      firstName: ['', UlaValidators.getAllFirstAndLastNameValidators_PLFlows(this.registerFormValidationService)],
      lastName: ['', UlaValidators.getAllFirstAndLastNameValidators_PLFlows(this.registerFormValidationService)],
      suffix: null,
      email: emailControl,
      address: ['', UlaValidators.getAllAddressValidators()],
      addressUnit: '',
      state: stateControl,
      city: ['', UlaValidators.getAllCityValidators_PLFlows(this.registerFormValidationService)],
      country: ['US'],
      county: [''],
      zipCode: ['', UlaValidators.getAllZipCodeValidators(this.registerFormValidationService)],

      // home payment fields
      occupancyStatus: occupancyStatusControl,
      paymentAmount: ['', UlaValidators.getHomePaymentValidator(occupancyStatusControl, this.route.snapshot.queryParams.lead)],

      // account setup fields
      dob: ['', UlaValidators.getAllDateValidators(this.registerFormValidationService, stateControl)],
      yearlyIncome: ['', UlaValidators.getAllYearlyIncomeValidators_PLFlows(this.registerFormValidationService)],
      phoneNumber: ['', UlaValidators.getAllPhoneValidators(this.registerFormValidationService)],
      phoneType: ['', UlaValidators.getAllPhoneTypeValidators()],
      password: passwordControl,
      confirmPassword: ['', UlaValidators.getRepeatPasswordValidators(passwordControl)],
      brandAwareness: null,
      consentContact: new FormControl(null, UlaValidators.mustBeChecked),
      contactAgreement: false
    });
  }

  private getReapplyFormGroup(): FormGroup {
    const occupancyStatusControl = new FormControl('', Validators.required);
    const stateControl = new FormControl('', [Validators.required, UlaValidators.getStateLicensingValidator(RegisterFormProvider.config)]);

    const loanAmountValidators = UlaValidators.getWelcomePageLoanAmountValidators_PersonalLoansFlows(
      this.registerFormValidationService,
      true);

    return this.formBuilder.group({
      loanAmount: ['', loanAmountValidators],
      loanPurpose: [null, Validators.required],
      projectType: [null, Validators.required],
      mortgageBorrowerGuid: [],

      // let's get started fields
      firstName: ['', UlaValidators.getAllFirstAndLastNameValidators_PLFlows(this.registerFormValidationService)],
      lastName: ['', UlaValidators.getAllFirstAndLastNameValidators_PLFlows(this.registerFormValidationService)],
      suffix: null,
      email: null,
      address: ['', UlaValidators.getAllAddressValidators()],
      addressUnit: '',
      state: stateControl,
      city: ['', UlaValidators.getAllCityValidators_PLFlows(this.registerFormValidationService)],
      country: ['US'],
      county: [''],
      zipCode: ['', UlaValidators.getAllZipCodeValidators(this.registerFormValidationService)],

      // home payment fields
      occupancyStatus: occupancyStatusControl,
      paymentAmount: ['', UlaValidators.getHomePaymentValidator(occupancyStatusControl, this.route.snapshot.queryParams['lead'])],

      // account setup fields
      dob: ['', UlaValidators.getAllDateValidators(this.registerFormValidationService, stateControl)],
      yearlyIncome: ['', UlaValidators.getAllYearlyIncomeValidators_PLFlows(this.registerFormValidationService)],
      phoneNumber: ['', UlaValidators.getAllPhoneValidators(this.registerFormValidationService)],

      // TODO: LDTSULA-2766 covers adding the phoneType field. It should not appear until it can be saved in the backend.
      phoneType: null,
      password: null,

      // the confirm password field has been removed from current design, but may come back, so leaving this here
      // confirmPassword: ['', UlaValidators.getRepeatPasswordValidators(passwordControl)],
      brandAwareness: null,
      consentContact: new FormControl(null, UlaValidators.mustBeChecked),
      contactAgreement: false
    });
  }
}
